import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Download, Flame, Sparkles, RefreshCw, Instagram, Lightbulb, Lock,
  ThumbsUp, ThumbsDown, ClipboardList, Clock, Heart, Crown, Gauge, LayoutDashboard,
  ArrowRight, TrendingUp, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import PaywallModal from "@/components/PaywallModal";
import AuthModal from "@/components/AuthModal";

const ADMIN_MODE_KEY = "nail_rater_admin_mode";

function ScoreGauge({ score, maxScore = 20 }) {
  const percentage = (score / maxScore) * 100;
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const color = percentage >= 75 ? "#10B981" : percentage >= 50 ? "#F59E0B" : "#E11D48";
  return (
    <div className="score-ring">
      <svg width="200" height="200" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r={radius} fill="none" stroke="rgba(190, 24, 93, 0.1)" strokeWidth="12" />
        <motion.circle cx="100" cy="100" r={radius} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={circumference} initial={{ strokeDashoffset: circumference }} animate={{ strokeDashoffset }} transition={{ duration: 1.5, ease: "easeOut" }} />
      </svg>
      <motion.span className="score-text font-playfair" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 }} data-testid="total-score">{score}</motion.span>
      <span className="score-label font-medium">out of {maxScore}</span>
    </div>
  );
}

function CategoryBar({ category, index }) {
  const percentage = (category.score / category.max_score) * 100;
  const color = percentage >= 80 ? "bg-green-500" : percentage >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + index * 0.1 }} className="glass rounded-2xl p-4" data-testid={`category-${index}`}>
      <div className="flex justify-between items-center mb-2">
        <span className="font-medium text-foreground">{category.name}</span>
        <span className="font-playfair font-bold text-lg text-primary">{category.score}/{category.max_score}</span>
      </div>
      <div className="score-bar"><motion.div className={`score-bar-fill ${color}`} initial={{ width: 0 }} animate={{ width: `${percentage}%` }} transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }} /></div>
      <p className="text-sm text-muted-foreground mt-2">{category.explanation}</p>
    </motion.div>
  );
}

function LockedCard({ title, onUnlock, children, testid }) {
  return (
    <div className="relative glass rounded-3xl p-6 mb-6 overflow-hidden" data-testid={testid}>
      <div className="blur-sm pointer-events-none select-none">{children}</div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40 backdrop-blur-[2px] text-center p-4">
        <Lock className="w-7 h-7 text-primary mb-2" />
        <p className="font-semibold text-foreground mb-3">{title}</p>
        <Button onClick={onUnlock} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`${testid}-unlock`}>Unlock with Premium</Button>
      </div>
    </div>
  );
}

export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const { user } = useAuth();

  const [rating, setRating] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [savageMode, setSavageMode] = useState(false);
  const [savageRoast, setSavageRoast] = useState(null);
  const [isLoadingRoast, setIsLoadingRoast] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const isAdmin = localStorage.getItem(ADMIN_MODE_KEY) === "true";
  const subscribed = user?.is_subscribed || isAdmin;

  useEffect(() => {
    if (location.state?.rating) {
      setRating(location.state.rating);
      setImagePreview(location.state.imagePreview);
      if (location.state.promptSignup) setTimeout(() => setShowAuth(true), 2500);
      else if (location.state.promptPaywall) setTimeout(() => setShowPaywall(true), 2500);
    } else {
      navigate("/");
    }
  }, [location, navigate]);

  const handleSavageModeToggle = async (checked) => {
    if (!subscribed) { setShowPaywall(true); return; }
    setSavageMode(checked);
    if (checked && !savageRoast && imagePreview) {
      setIsLoadingRoast(true);
      try {
        const base64 = imagePreview.split(",")[1];
        const { data } = await api.post("/analyze", { image_base64: base64, savage_mode: true });
        setSavageRoast(data.savage_roast);
      } catch (e) { toast.error("Couldn't get the roast. Try again!"); }
      setIsLoadingRoast(false);
    }
  };

  const wrapText = (ctx, text, maxWidth) => {
    const words = (text || "").split(" ");
    const lines = []; let cur = "";
    words.forEach((w) => {
      const test = cur + w + " ";
      if (ctx.measureText(test).width > maxWidth && cur !== "") { lines.push(cur.trim()); cur = w + " "; }
      else cur = test;
    });
    lines.push(cur.trim());
    return lines;
  };

  const generateShareImage = useCallback(() => {
    if (!canvasRef.current || !rating) return null;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const W = 1080, H = 1920;
    canvas.width = W; canvas.height = H;

    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#FFF0F5"); bg.addColorStop(0.55, "#FBCFE8"); bg.addColorStop(1, "#F5D0E8");
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    // Decorative blurred blobs
    const blob = (x, y, r, color) => { ctx.save(); ctx.globalAlpha = 0.5; ctx.filter = "blur(60px)"; ctx.fillStyle = color; ctx.beginPath(); ctx.arc(x, y, r, 0, 2 * Math.PI); ctx.fill(); ctx.restore(); };
    blob(160, 220, 140, "#F9A8D4"); blob(920, 300, 120, "#C4B5FD");

    // Inner card
    const cardX = 70, cardY = 150, cardW = W - 140, cardH = H - 360, rad = 60;
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.beginPath();
    ctx.moveTo(cardX + rad, cardY);
    ctx.arcTo(cardX + cardW, cardY, cardX + cardW, cardY + cardH, rad);
    ctx.arcTo(cardX + cardW, cardY + cardH, cardX, cardY + cardH, rad);
    ctx.arcTo(cardX, cardY + cardH, cardX, cardY, rad);
    ctx.arcTo(cardX, cardY, cardX + cardW, cardY, rad);
    ctx.closePath(); ctx.fill();
    ctx.restore();

    ctx.textAlign = "center";
    // Brand
    ctx.fillStyle = "#BE185D"; ctx.font = "600 34px 'Manrope', sans-serif";
    ctx.fillText("N A I L   C O A C H", W / 2, cardY + 90);
    ctx.fillStyle = "#4A044E"; ctx.font = "700 52px 'Playfair Display', serif";
    ctx.fillText("My Nail Score", W / 2, cardY + 165);

    // Score ring
    const cx = W / 2, cy = cardY + 400, r = 170;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, 2 * Math.PI); ctx.strokeStyle = "rgba(190,24,93,0.12)"; ctx.lineWidth = 26; ctx.stroke();
    const pct = (rating.total_score / 20) * 100;
    ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI / 2, (pct / 100) * 2 * Math.PI - Math.PI / 2);
    ctx.strokeStyle = pct >= 75 ? "#10B981" : pct >= 50 ? "#F59E0B" : "#E11D48"; ctx.lineWidth = 26; ctx.lineCap = "round"; ctx.stroke();
    ctx.fillStyle = "#4A044E"; ctx.font = "700 140px 'Playfair Display', serif"; ctx.fillText(String(rating.total_score), cx, cy + 45);
    ctx.font = "500 40px 'Manrope', sans-serif"; ctx.fillStyle = "#831843"; ctx.fillText("out of 20", cx, cy + 105);

    // Potential pill
    if (rating.potential_score != null) {
      const label = `Potential  ${rating.potential_score}/20`;
      ctx.font = "700 34px 'Manrope', sans-serif";
      const pw = ctx.measureText(label).width + 90;
      const px = cx - pw / 2, py = cy + r + 70;
      ctx.fillStyle = "#DCFCE7";
      ctx.beginPath(); ctx.roundRect(px, py, pw, 66, 33); ctx.fill();
      ctx.fillStyle = "#15803D"; ctx.fillText(label, cx, py + 44);
    }

    // Categories with mini bars
    let y = cardY + 900;
    ctx.textAlign = "left";
    rating.categories.forEach((c) => {
      ctx.fillStyle = "#4A044E"; ctx.font = "600 34px 'Manrope', sans-serif"; ctx.fillText(c.name, cardX + 70, y);
      ctx.textAlign = "right"; ctx.fillStyle = "#BE185D"; ctx.font = "700 34px 'Playfair Display', serif"; ctx.fillText(`${c.score}/5`, cardX + cardW - 70, y);
      ctx.textAlign = "left";
      const barX = cardX + 70, barY = y + 22, barW = cardW - 140;
      ctx.fillStyle = "rgba(190,24,93,0.12)"; ctx.beginPath(); ctx.roundRect(barX, barY, barW, 16, 8); ctx.fill();
      ctx.fillStyle = "#EC4899"; ctx.beginPath(); ctx.roundRect(barX, barY, barW * (c.score / 5), 16, 8); ctx.fill();
      y += 110;
    });

    // Summary
    ctx.textAlign = "center"; ctx.fillStyle = "#831843"; ctx.font = "italic 34px 'Manrope', sans-serif";
    wrapText(ctx, rating.summary, cardW - 160).slice(0, 3).forEach((l, i) => ctx.fillText(l, W / 2, y + 30 + i * 46));

    if (savageMode && savageRoast) {
      ctx.fillStyle = "#E11D48"; ctx.font = "500 40px 'Caveat', cursive";
      wrapText(ctx, `🌶️ ${savageRoast}`, cardW - 160).slice(0, 2).forEach((l, i) => ctx.fillText(l, W / 2, y + 190 + i * 50));
    }

    // Footer
    ctx.fillStyle = "#BE185D"; ctx.font = "700 40px 'Manrope', sans-serif";
    ctx.fillText("Rate & improve yours at Nail Coach 💅", W / 2, H - 120);

    return canvas.toDataURL("image/png");
  }, [rating, savageMode, savageRoast]);

  const handleDownload = () => {
    const url = generateShareImage();
    if (!url) return;
    const link = document.createElement("a"); link.download = `nail-score-${rating.total_score}.png`; link.href = url; link.click();
    toast.success("Image downloaded! Share it on Instagram/TikTok 💅");
  };

  const handleShare = async () => {
    const url = generateShareImage();
    if (!url) return;
    const blob = await (await fetch(url)).blob();
    const file = new File([blob], "nail-score.png", { type: "image/png" });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ title: `My Nail Score: ${rating.total_score}/20`, text: `I got ${rating.total_score}/20 on Nail Coach! 💅`, files: [file] });
      } catch (e) { if (e.name !== "AbortError") handleDownload(); }
    } else handleDownload();
  };

  if (!rating) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center"><Sparkles className="w-12 h-12 text-primary mx-auto animate-spin" /><p className="mt-4 text-muted-foreground">Loading results...</p></div>
      </div>
    );
  }

  const coaching = rating.coaching;
  const beauty = rating.beauty_report;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="results-page">
      <canvas ref={canvasRef} className="hidden" />
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-muted-foreground hover:text-foreground" data-testid="back-btn"><ArrowLeft className="w-5 h-5 mr-2" />Back</Button>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Savage Mode</span>
            <div className="relative">
              <Switch checked={savageMode} onCheckedChange={handleSavageModeToggle} disabled={isLoadingRoast} className={savageMode ? "bg-rose-500" : ""} data-testid="savage-toggle" />
              {!subscribed && <Lock className="w-3 h-3 text-muted-foreground absolute -top-1 -right-1" />}
            </div>
            <Flame className={`w-5 h-5 transition-colors ${savageMode ? "text-rose-500" : "text-muted-foreground"}`} />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-heavy rounded-3xl p-8 text-center mb-6">
          <h2 className="font-playfair text-2xl font-bold text-foreground mb-6">Your Nail Score</h2>
          <ScoreGauge score={rating.total_score} />
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-6 text-foreground/80 max-w-md mx-auto" data-testid="summary">{rating.summary}</motion.p>

          {rating.potential_score != null && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }} className="mt-6 max-w-sm mx-auto glass rounded-2xl p-4" data-testid="potential-score">
              <div className="flex items-center justify-center gap-5">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Now</p>
                  <p className="font-playfair text-3xl font-bold text-foreground">{rating.total_score}</p>
                </div>
                <ArrowRight className="w-6 h-6 text-primary" />
                <div className="text-center">
                  <p className="text-xs text-green-600 uppercase tracking-wide font-semibold">Your potential</p>
                  <p className="font-playfair text-3xl font-bold text-green-600">{rating.potential_score}</p>
                </div>
              </div>
              <p className="text-xs text-center text-muted-foreground mt-2">You could reach <b className="text-green-600">{rating.potential_score}/20</b> — here's how 👇</p>
            </motion.div>
          )}
          <AnimatePresence>
            {savageMode && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-4 p-4 bg-rose-50 rounded-2xl border border-rose-200">
                <div className="flex items-center gap-2 justify-center mb-2"><Flame className="w-5 h-5 text-rose-500" /><span className="font-semibold text-rose-600">Savage Roast</span></div>
                {isLoadingRoast ? <p className="text-rose-600 analyzing-text">Getting spicy... 🌶️</p> : <p className="font-caveat text-lg text-rose-700" data-testid="savage-roast">{savageRoast || "Your nails are... interesting. 💀"}</p>}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Category Breakdown */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6">
          <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Score Breakdown</h3>
          <div className="space-y-3">{rating.categories.map((c, i) => <CategoryBar key={c.name} category={c} index={i} />)}</div>
        </motion.div>

        {/* Improvement Tips */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="glass rounded-3xl p-6 mb-6">
          <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" />Quick Tips</h3>
          <ul className="space-y-3">
            {rating.improvement_tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-3 text-foreground/80"><span className="text-primary font-bold">{i + 1}.</span><span>{tip}</span></li>
            ))}
          </ul>
        </motion.div>

        {/* Path to your potential — premium */}
        {rating.improvement_path && rating.improvement_path.length > 0 && (
          subscribed ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-6 mb-6" data-testid="potential-path">
              <h3 className="font-playfair text-xl font-bold text-foreground mb-1 flex items-center gap-2"><Target className="w-5 h-5 text-green-600" />Path to {rating.potential_score}/20</h3>
              <p className="text-sm text-muted-foreground mb-4">Make these changes to close the gap:</p>
              <div className="space-y-3">
                {rating.improvement_path.map((step, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 bg-secondary/30 rounded-2xl p-3">
                    <div className="flex items-start gap-3"><span className="w-6 h-6 shrink-0 rounded-full bg-green-100 text-green-700 text-sm flex items-center justify-center font-semibold">{i + 1}</span><span className="text-sm text-foreground/80">{step.action}</span></div>
                    <span className="shrink-0 inline-flex items-center gap-1 text-green-600 font-semibold text-sm"><TrendingUp className="w-4 h-4" />+{step.points}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ) : (
            <LockedCard title="Unlock your step-by-step path to a higher score" onUnlock={() => setShowPaywall(true)} testid="potential-path-locked">
              <h3 className="font-playfair text-xl font-bold mb-2 flex items-center gap-2"><Target className="w-5 h-5 text-green-600" />Path to {rating.potential_score}/20</h3>
              <p className="text-foreground/80">The exact changes — each with how many points it adds — to take you from {rating.total_score} to {rating.potential_score}/20.</p>
            </LockedCard>
          )
        )}

        {/* AI Coaching Plan — requires account */}
        {coaching && (
          user ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-6 mb-6" data-testid="coaching-section">
              <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" />Your AI Coaching Plan</h3>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div className="bg-green-50 rounded-2xl p-4">
                  <p className="flex items-center gap-2 font-semibold text-green-700 mb-2"><ThumbsUp className="w-4 h-4" />Strengths</p>
                  <ul className="space-y-1 text-sm text-foreground/80">{(coaching.strengths || []).map((s, i) => <li key={i}>• {s}</li>)}</ul>
                </div>
                <div className="bg-rose-50 rounded-2xl p-4">
                  <p className="flex items-center gap-2 font-semibold text-rose-700 mb-2"><ThumbsDown className="w-4 h-4" />Focus areas</p>
                  <ul className="space-y-1 text-sm text-foreground/80">{(coaching.weaknesses || []).map((s, i) => <li key={i}>• {s}</li>)}</ul>
                </div>
              </div>
              <p className="font-semibold text-foreground mb-2">Top 5 recommendations</p>
              <ol className="space-y-2 mb-4">{(coaching.recommendations || []).map((rec, i) => (
                <li key={i} className="flex items-start gap-3 text-foreground/80"><span className="w-6 h-6 shrink-0 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-semibold">{i + 1}</span>{rec}</li>
              ))}</ol>
              {coaching.estimated_improvement_time && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3"><Clock className="w-4 h-4 text-primary" />Estimated time to improve: <span className="font-medium text-foreground">{coaching.estimated_improvement_time}</span></div>
              )}
              {coaching.coach_message && <p className="italic text-foreground/80 bg-secondary/40 rounded-2xl p-4">💬 {coaching.coach_message}</p>}
            </motion.div>
          ) : (
            <LockedCard title="Create a free account to unlock your full AI coaching plan" onUnlock={() => setShowAuth(true)} testid="coaching-locked">
              <h3 className="font-playfair text-xl font-bold mb-4 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" />Your AI Coaching Plan</h3>
              <p className="text-foreground/80">Personalized strengths, focus areas, top 5 recommendations and a coaching note to help you improve every week.</p>
            </LockedCard>
          )
        )}

        {/* AI Beauty Report — premium */}
        {beauty && (
          subscribed ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-6 mb-6" data-testid="beauty-section">
              <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" />AI Beauty Report</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between bg-secondary/30 rounded-xl p-3"><span className="flex items-center gap-2 text-muted-foreground"><Gauge className="w-4 h-4" />Confidence score</span><span className="font-playfair font-bold text-lg text-primary">{beauty.confidence_score}/100</span></div>
                <div className="flex items-center justify-between bg-secondary/30 rounded-xl p-3"><span className="flex items-center gap-2 text-muted-foreground"><Sparkles className="w-4 h-4" />Estimated salon quality</span><span className="font-medium text-foreground">{beauty.salon_quality_estimate}</span></div>
                <div className="bg-secondary/30 rounded-xl p-3"><p className="flex items-center gap-2 text-muted-foreground mb-1"><Heart className="w-4 h-4" />Nail health</p><p className="text-foreground/80">{beauty.nail_health}</p></div>
                <div className="bg-secondary/30 rounded-xl p-3"><p className="text-muted-foreground mb-1">Professional impression</p><p className="text-foreground/80">{beauty.professional_impression}</p></div>
              </div>
            </motion.div>
          ) : (
            <LockedCard title="Unlock your premium AI Beauty Report" onUnlock={() => setShowPaywall(true)} testid="beauty-locked">
              <h3 className="font-playfair text-xl font-bold mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" />AI Beauty Report</h3>
              <p className="text-foreground/80">Nail health, professional impression, overall confidence score and estimated salon quality.</p>
            </LockedCard>
          )
        )}

        {/* Actions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={handleShare} className="share-btn bg-gradient-to-r from-pink-500 to-purple-500 text-white" data-testid="share-btn"><Instagram className="w-5 h-5" />Share</Button>
          <Button onClick={handleDownload} variant="outline" className="share-btn border-primary text-primary hover:bg-primary/5" data-testid="download-btn"><Download className="w-5 h-5" />Download</Button>
          {user ? (
            <Button onClick={() => navigate("/dashboard")} variant="ghost" className="share-btn text-muted-foreground hover:text-foreground" data-testid="dashboard-btn"><LayoutDashboard className="w-5 h-5" />Dashboard</Button>
          ) : (
            <Button onClick={() => navigate("/")} variant="ghost" className="share-btn text-muted-foreground hover:text-foreground" data-testid="rate-another-btn"><RefreshCw className="w-5 h-5" />Rate Another</Button>
          )}
        </motion.div>
      </div>

      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
      <AuthModal isOpen={showAuth} mode="signup" title="Loved your rating? Create a free account" subtitle="Get one more free analysis + your full AI coaching plan" onClose={() => setShowAuth(false)} />
    </div>
  );
}
